/**
 * Development-only guards for the Calendar EXTREME virtualization contract.
 * Always active when NODE_ENV !== "production". Also usable with session debug.
 */

import {
   ULTRA_DEV_POOL_SLOT_REMOUNT_BUDGET,
   ULTRA_DEV_VIEWPORT_MOUNT_BUDGET,
   isUltraVirtualizationDevMode,
   validatePoolSlotKey,
} from "../scroll/ultraVirtualizationContract";
import { auditUltraGridDom, getUltraRenderStats } from "./ultraRenderAudit";

const poolSlotMountCounts = new Map();
let viewportMountTimestamps = [];
let observer = null;
let rafWatchId = 0;
let lastWarnSig = "";
let lastWarnAt = 0;

function shouldGuard() {
   if (typeof window === "undefined") return false;
   if (isUltraVirtualizationDevMode()) return true;
   try {
      return localStorage.getItem("__ULTRA_SESSION_DEBUG") === "1";
   } catch {
      return false;
   }
}

function warn(code, detail) {
   if (!shouldGuard()) return;
   const sig = `${code}:${typeof detail === "string" ? detail : JSON.stringify(detail)}`;
   const now = Date.now();
   // Dedupe identical warnings within 2s to avoid scroll spam.
   if (sig === lastWarnSig && now - lastWarnAt < 2000) return;
   lastWarnSig = sig;
   lastWarnAt = now;
   // eslint-disable-next-line no-console
   console.warn(
      `[ULTRA:VIRTUALIZATION] ${code}`,
      detail ?? "",
      "\nSee src/calendar/scroll/ultraVirtualizationContract.js",
   );
}

export function assertStablePoolSlotKey(key, poolIndex) {
   if (!shouldGuard()) return true;
   const result = validatePoolSlotKey(key, poolIndex);
   if (!result.ok) {
      warn("UNSTABLE_POOL_KEY", result.reason);
      return false;
   }
   return true;
}

export function notePoolSlotMount(poolIndex) {
   if (!shouldGuard()) return;
   const index = Math.trunc(Number(poolIndex) || 0);
   const next = (poolSlotMountCounts.get(index) || 0) + 1;
   poolSlotMountCounts.set(index, next);
   if (next > ULTRA_DEV_POOL_SLOT_REMOUNT_BUDGET + 1) {
      // +1 allows StrictMode double-invoke on first mount
      warn("POOL_SLOT_REMOUNT", {
         poolIndex: index,
         mounts: next,
         budget: ULTRA_DEV_POOL_SLOT_REMOUNT_BUDGET,
         hint: "Pool keys must stay pool-{index}. Do not bake contentRefreshToken into the key.",
      });
   }
}

export function notePoolSlotUnmount(poolIndex) {
   if (!shouldGuard()) return;
   void poolIndex;
   // Counts stay cumulative per viewport session; reset on viewport unmount.
}

export function noteViewportMountedForContract() {
   if (!shouldGuard()) return;
   const now = Date.now();
   viewportMountTimestamps = viewportMountTimestamps.filter((t) => now - t < 15000);
   viewportMountTimestamps.push(now);
   if (viewportMountTimestamps.length > ULTRA_DEV_VIEWPORT_MOUNT_BUDGET) {
      warn("VIEWPORT_REMOUNT_STORM", {
         mountsIn15s: viewportMountTimestamps.length,
         budget: ULTRA_DEV_VIEWPORT_MOUNT_BUDGET,
         hint: "Do not key UltraStructureGrid on scrollHostVersion / zoom / month tokens.",
      });
   }
}

export function resetPoolSlotMountCounts() {
   poolSlotMountCounts.clear();
}

export function runUltraVirtualizationDomGuard(root = null) {
   if (!shouldGuard()) return null;
   const snapshot = auditUltraGridDom(root);
   if (!snapshot?.ok) {
      warn("DOM_INCONSISTENT", {
         problems: snapshot.problems,
         grids: snapshot.grids,
         scrollHosts: snapshot.scrollHosts,
         selectionBoxes: snapshot.selectionBoxes,
         overlappingCells: snapshot.duplicateHourPairs,
         duplicateSlotKeys: snapshot.duplicateSlotKeys,
         reservationLayers: snapshot.reservationLayers,
      });
   }
   if (snapshot?.grids > 1) {
      warn("DUPLICATE_GRID", { grids: snapshot.grids });
   }
   if (snapshot?.scrollHosts > 1) {
      warn("DUPLICATE_SCROLL_HOST", { scrollHosts: snapshot.scrollHosts });
   }
   if (snapshot?.selectionBoxes > Math.max(1, snapshot.grids)) {
      warn("DUPLICATE_SELECTION_OVERLAY", {
         selectionBoxes: snapshot.selectionBoxes,
         grids: snapshot.grids,
      });
   }
   if (snapshot?.reservationLayers > 1) {
      warn("DUPLICATE_RESERVATION_LAYER", {
         reservationLayers: snapshot.reservationLayers,
      });
   }
   // Overlapping pool cells at the same (x,y) — real ghost/duplicate DOM.
   // Same hour label on one row across columns is normal and must not warn.
   if (snapshot?.duplicateHourPairs?.length) {
      warn("OVERLAPPING_POOL_CELLS", snapshot.duplicateHourPairs.slice(0, 8));
   }
   if (snapshot?.duplicateSlotKeys?.length) {
      warn("DUPLICATE_SLOT_KEYS", snapshot.duplicateSlotKeys.slice(0, 8));
   }
   return snapshot;
}

/**
 * Watch the live Extreme calendar DOM for contract violations.
 * Safe to call multiple times — installs a single observer.
 */
export function installUltraVirtualizationDevGuard(rootSelector = ".ultra-view") {
   if (!shouldGuard() || typeof document === "undefined") {
      return () => {};
   }
   if (observer) return () => {};

   const scheduleCheck = () => {
      if (rafWatchId) return;
      rafWatchId = window.requestAnimationFrame(() => {
         rafWatchId = 0;
         const root = document.querySelector(rootSelector);
         if (!root) return;
         runUltraVirtualizationDomGuard(root);
         const stats = getUltraRenderStats();
         if (stats.viewportAliveTracked > 1) {
            warn("OVERLAPPING_VIEWPORTS", {
               alive: stats.viewportAliveTracked,
            });
         }
      });
   };

   observer = new MutationObserver(scheduleCheck);
   observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
   });
   scheduleCheck();

   return () => {
      observer?.disconnect();
      observer = null;
      if (rafWatchId) {
         window.cancelAnimationFrame(rafWatchId);
         rafWatchId = 0;
      }
   };
}

export function installUltraVirtualizationDevApi(apiTarget) {
   if (!apiTarget || typeof apiTarget !== "object") return;
   apiTarget.guardDom = (root) => runUltraVirtualizationDomGuard(root);
   apiTarget.installDevGuard = installUltraVirtualizationDevGuard;
}
